import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@csa/web-foundation";
import type { QueueBundle, QueueFilter, QueueItem, TransitionInput } from "./types";

// The queue is keyed by its filter so band/type changes refetch the right slice.
const queueKey = (filter: QueueFilter) => ["queue", filter.band ?? null, filter.type ?? null];

function queueQueryString(filter: QueueFilter): string {
  const p = new URLSearchParams();
  if (filter.band) p.set("band", filter.band);
  if (filter.type) p.set("type", filter.type);
  const q = p.toString();
  return q ? `?${q}` : "";
}

/** GET /v1/queue — the acting advisor's prioritized, bundled, non-suppressed
 *  queue (own→team scope, enforced by the API). The read is enriched with
 *  partyName + plate per row, and NEW_LEAD items surface self-serve web leads
 *  inline — no separate /v1/leads or /v1/assets fan-out is needed. */
export function useQueue(filter: QueueFilter = {}) {
  const api = useApi();
  return useQuery({
    queryKey: queueKey(filter),
    queryFn: () => api.get<QueueBundle[]>(`/v1/queue${queueQueryString(filter)}`),
  });
}

/** A unique, contract-valid idempotency key ([A-Za-z0-9_:.-], ≤200). */
function newIdempotencyKey(): string {
  const uuid = globalThis.crypto?.randomUUID?.();
  return `q-${uuid ?? `${Date.now()}-${Math.round(performance.now() * 1000)}`}`;
}

/** PATCH /v1/queue-items/:id — claim / complete / dismiss. Every call is an
 *  explicit advisor action (suggest-not-decide) carrying a client-generated
 *  idempotency-key (safe retries). On settle we refetch the queue so the new
 *  state is authoritative; the call rides the governance client, so a step-up /
 *  approval challenge is already handled centrally. */
export function useQueueTransition() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: TransitionInput }) =>
      api.patch<QueueItem>(`/v1/queue-items/${id}`, input, { idempotencyKey: newIdempotencyKey() }),
    onSettled: () => qc.invalidateQueries({ queryKey: ["queue"] }),
  });
}
